import { type CopySink, type CopySource, Dataset, type ICopySinkWithUpdateMethod, Metamodel, TabularCopySource, UpdateMethod } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { GlobalVariables } from 'app/services/globals';
import {
    deltaCloneLinkedServiceTypes,
    deltaFullLinkedServiceTypes,
    type DeltaTier
} from 'app/authoring/copy-job/wizard/copy-job-delta.const';

/**
 * Delta tier resolution is kept free of dataset-metadata and registry imports so that the model layer can reuse
 * it: the dataset metadata modules are themselves part of the import graph of everything else in the copy job
 * wizard, and pulling them in here would close a cycle.
 */

export interface ICopyJobEndpointTypes {
    sourceLinkedServiceType?: string;
    sourceDatasetType?: string;
    sinkLinkedServiceType?: string;
    sinkDatasetType?: string;
    hasCustomQuery?: boolean;
}


export const deltaCapableSinkLinkedServiceTypes = (): string[] => [
    ...(GlobalVariables.supportDelta ? deltaFullLinkedServiceTypes : []),
    ...(GlobalVariables.supportDeltaCloneCopyJob ? deltaCloneLinkedServiceTypes : [])
];

/** Among file formats, clone-only Delta sources require Delta; Lakehouse Tables are also valid sinks. */
export const sinkOnlySupportsDeltaFormat = (context: ICopyJobEndpointTypes): boolean =>
    context.sourceDatasetType === adfModelTypeMap.delta.datasetType &&
    resolveDeltaSourceTier(context.sourceLinkedServiceType) === 'clone' &&
    resolveDeltaSinkTier(context) === 'clone';

export const resolveDeltaSourceTier = (sourceLinkedServiceType?: string): DeltaTier => {
    // Full support is tried first: a store can be in both lists, and the full experience is the richer one.
    if (GlobalVariables.supportDelta && deltaFullLinkedServiceTypes.includes(sourceLinkedServiceType)) {
        return 'full';
    }
    if (GlobalVariables.supportDeltaCloneCopyJob && deltaCloneLinkedServiceTypes.includes(sourceLinkedServiceType)) {
        return 'clone';
    }
    return 'none';
};

/** Editing fixes the destination connector, but not its file format. Lakehouse Files and Tables both permit clones. */
export const sourceSupportsDeltaFormat = (context: ICopyJobEndpointTypes, isEditMode: boolean = false): boolean => {
    const sourceTier = resolveDeltaSourceTier(context.sourceLinkedServiceType);
    return sourceTier === 'full' ||
        (sourceTier === 'clone' && (!isEditMode || deltaCapableSinkLinkedServiceTypes().includes(context.sinkLinkedServiceType)));
};

/**
 * Delta-to-Delta uses full support only when both endpoints support it. A clone-only source stays a clone,
 * and a full source falls back to clone for a clone-only destination when the clone feature is enabled.
 * Otherwise the sink's own store decides.
 * Omit the sink dataset type for capability checks before choosing a format.
 */
export const resolveDeltaSinkTier = (context: ICopyJobEndpointTypes): DeltaTier => {
    if (context.sinkDatasetType != null && context.sinkDatasetType !== adfModelTypeMap.delta.datasetType) {
        return 'none';
    }
    const { sourceLinkedServiceType, sourceDatasetType, sinkLinkedServiceType, hasCustomQuery } = context;
    const cloneEnabled = GlobalVariables.supportDeltaCloneCopyJob;
    const fullEnabled = GlobalVariables.supportDelta;

    if (sourceDatasetType === adfModelTypeMap.delta.datasetType) {
        const sourceTier = resolveDeltaSourceTier(sourceLinkedServiceType);
        if (sourceTier === 'clone' && deltaCapableSinkLinkedServiceTypes().includes(sinkLinkedServiceType)) {
            return 'clone';
        }
        if (sourceTier === 'full' && deltaFullLinkedServiceTypes.includes(sinkLinkedServiceType)) {
            return 'full';
        }
        if (sourceTier === 'full' && cloneEnabled && deltaCloneLinkedServiceTypes.includes(sinkLinkedServiceType)) {
            return 'clone';
        }
        return 'none';
    }

    if (fullEnabled && deltaFullLinkedServiceTypes.includes(sinkLinkedServiceType)) {
        return 'full';
    }
    if (
        cloneEnabled &&
        deltaCloneLinkedServiceTypes.includes(sinkLinkedServiceType) &&
        // The only non-Delta source the clone runtime can read is a lakehouse table, and never through a custom query.
        sourceDatasetType === adfModelTypeMap.lakehouseTable.datasetType &&
        !hasCustomQuery
    ) {
        return 'clone';
    }
    return 'none';
};

export const destinationSupportsDeltaFormat = (context: ICopyJobEndpointTypes): boolean =>
    resolveDeltaSinkTier(context) !== 'none';

/** Classify selected endpoints without treating Lakehouse Tables as a Delta file format. */
export const isDeltaCloneCopyEndpoints = (context: ICopyJobEndpointTypes): boolean =>
    (context.sinkDatasetType === adfModelTypeMap.delta.datasetType && resolveDeltaSinkTier(context) === 'clone') ||
    (context.sinkDatasetType === adfModelTypeMap.lakehouseTable.datasetType &&
        context.sinkLinkedServiceType === adfModelTypeMap.lakehouse.linkedServiceType &&
        context.sourceDatasetType === adfModelTypeMap.delta.datasetType &&
        resolveDeltaSourceTier(context.sourceLinkedServiceType) === 'clone');

interface IDeltaCopyActivity {
    sourceDataset?: Dataset;
    sinkDataset?: Dataset;
    source?: CopySource;
    copySink?: CopySink;
    readMethod?: string;
}

const getEndpointTypes = (activity: IDeltaCopyActivity): ICopyJobEndpointTypes => ({
    sourceLinkedServiceType: activity.sourceDataset?.linkedService?.getLinkedServiceType(),
    sourceDatasetType: Metamodel.modelOf(activity.sourceDataset)?.name,
    sinkDatasetType: Metamodel.modelOf(activity.sinkDataset)?.name,
    sinkLinkedServiceType: activity.sinkDataset?.linkedService?.getLinkedServiceType(),
    hasCustomQuery: activity.source instanceof TabularCopySource && activity.source.mode === 'query'
});

export const isDeltaCloneCopy = (activity?: IDeltaCopyActivity): boolean =>
    activity != null && isDeltaCloneCopyEndpoints(getEndpointTypes(activity));

/** Undefined retains the destination's normal key requirement. This does not change clone-tier restrictions. */
export const getCloneCompatibleUpsertKeyRequirement = (activity?: IDeltaCopyActivity): boolean | undefined => {
    if (activity == null) {
        return undefined;
    }
    const endpoints = getEndpointTypes(activity);
    if (isDeltaCloneCopyEndpoints(endpoints)) {
        // Clone copy pattern, keep optional upsert columns for backward compatibility
        return false;
    }
    if (resolveDeltaSinkTier(endpoints) === 'full' && endpoints.sinkDatasetType === adfModelTypeMap.delta.datasetType) {
        // Clone-compatible sink, keep optional upsert columns for backward compatibility
        return false;
    }
    if (endpoints.sinkDatasetType === adfModelTypeMap.lakehouseTable.datasetType &&
        endpoints.sourceDatasetType === adfModelTypeMap.delta.datasetType &&
        resolveDeltaSourceTier(endpoints.sourceLinkedServiceType) === 'full') {
        // Clone-compatible sink, keep optional upsert columns for backward compatibility
        return false;
    }
    return undefined;
};

/** Full Delta incremental Merge can omit keys, but must configure them before adding mappings. */
export const isDeltaMappingBlockedByMissingKeys = (
    activity?: IDeltaCopyActivity,
    isIncrementalCopy = activity?.readMethod === 'SnapshotPlusIncremental'
): boolean => {
    const sink = activity?.copySink as ICopySinkWithUpdateMethod | undefined;
    return isIncrementalCopy &&
        sink?.updateMethod === UpdateMethod.Upsert &&
        !isDeltaCloneCopy(activity) &&
        getCloneCompatibleUpsertKeyRequirement(activity) === false &&
        !sink.upsertKeyColumns?.some((key) => key?.trim());
};
